import { useState } from 'react';
import type { PickerOption } from '../lib/personas';

interface AvatarProps {
  persona: PickerOption;
  /** Tailwind size class, e.g. 'h-16 w-16'。 */
  className?: string;
  /** 圓角 / 方形 / 預設使用 :root --radius-card */
  shape?: 'circle' | 'card';
  /** 是否顯示 alt 文字。 */
  alt?: string;
}

const IMAGE2_AVATARS: Partial<Record<PickerOption, string>> = {
  luna: '/image2/readers/generated/avatar-luna.jpg',
  orion: '/image2/readers/generated/avatar-orion.jpg',
  sage: '/image2/readers/generated/avatar-sage.jpg',
};

export default function Avatar({
  persona,
  className = 'h-16 w-16',
  shape = 'circle',
  alt,
}: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const radiusClass = shape === 'circle' ? 'rounded-full' : 'rounded-card';

  if (errored) {
    return (
      <div
        aria-label={alt ?? persona}
        role="img"
        className={[
          'relative flex items-center justify-center overflow-hidden',
          radiusClass,
          className,
        ].join(' ')}
        style={{ background: PERSONA_BG[persona] }}
      >
        <PersonaMotif persona={persona} />
      </div>
    );
  }

  const image2Src = IMAGE2_AVATARS[persona] ?? `/avatars/${persona}.png`;

  return (
    <img
      src={image2Src}
      alt={alt ?? persona}
      onError={() => setErrored(true)}
      className={['object-cover', radiusClass, className].join(' ')}
      loading="lazy"
    />
  );
}

/** 每個 persona fallback 的底色 — 不再用 SaaS 漸層，改實心或雙色 ceramic 感。 */
const PERSONA_BG: Record<PickerOption, string> = {
  luna: 'linear-gradient(160deg, #f7f1e8 0%, #e0cfc2 100%)',
  orion: 'linear-gradient(160deg, #1a1628 0%, #0d0b14 100%)',
  sage: '#faf7f2',
  silhouette: 'linear-gradient(160deg, #2a2a2a 0%, #1a1a1a 100%)',
};

function PersonaMotif({ persona }: { persona: PickerOption }) {
  switch (persona) {
    case 'luna':
      return (
        <svg
          viewBox="0 0 64 64"
          width="62%"
          height="62%"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {/* Crescent moon — soft dusty rose */}
          <defs>
            <mask id="luna-mask">
              <rect width="64" height="64" fill="#fff" />
              <circle cx="40" cy="32" r="18" fill="#000" />
            </mask>
          </defs>
          <circle cx="32" cy="32" r="18" fill="#b87a6f" mask="url(#luna-mask)" />
          <circle cx="46" cy="22" r="1.4" fill="#b87a6f" opacity="0.55" />
          <circle cx="50" cy="42" r="1" fill="#b87a6f" opacity="0.4" />
        </svg>
      );
    case 'orion':
      return (
        <svg
          viewBox="0 0 64 64"
          width="68%"
          height="68%"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {/* Big Dipper — antique gold star points + thin connecting lines */}
          <g stroke="#c9a24a" strokeWidth="0.6" opacity="0.8">
            <line x1="14" y1="44" x2="22" y2="38" />
            <line x1="22" y1="38" x2="32" y2="32" />
            <line x1="32" y1="32" x2="42" y2="30" />
            <line x1="42" y1="30" x2="48" y2="22" />
            <line x1="48" y1="22" x2="50" y2="14" />
            <line x1="50" y1="14" x2="44" y2="10" />
          </g>
          <g fill="#c9a24a">
            <circle cx="14" cy="44" r="1.6" />
            <circle cx="22" cy="38" r="1.4" />
            <circle cx="32" cy="32" r="2" />
            <circle cx="42" cy="30" r="1.4" />
            <circle cx="48" cy="22" r="1.6" />
            <circle cx="50" cy="14" r="1.2" />
            <circle cx="44" cy="10" r="1.4" />
          </g>
          {/* faint outer ring */}
          <circle cx="32" cy="32" r="26" fill="none" stroke="#c9a24a" strokeWidth="0.3" opacity="0.3" />
        </svg>
      );
    case 'sage':
      return (
        <svg
          viewBox="0 0 64 64"
          width="56%"
          height="56%"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {/* Nested triangles — geometric, mono ink */}
          <g stroke="#0a0a0a" strokeWidth="1" fill="none">
            <polygon points="32,12 52,46 12,46" />
            <polygon points="32,22 44,42 20,42" />
            <line x1="32" y1="22" x2="32" y2="42" strokeWidth="0.5" />
          </g>
          <circle cx="32" cy="34" r="1.6" fill="#8a2a2a" />
        </svg>
      );
    case 'silhouette':
    default:
      return (
        <svg
          viewBox="0 0 64 64"
          width="68%"
          height="68%"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {/* Half-silhouette — partial profile in negative space */}
          <defs>
            <linearGradient id="silh-fade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#5a5a5a" stopOpacity="0.85" />
              <stop offset="1" stopColor="#5a5a5a" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <path
            d="M22 14 Q 18 22 18 30 Q 18 36 22 40 L 24 44 L 24 50 L 36 50 L 36 14 Z"
            fill="url(#silh-fade)"
          />
          <line x1="36" y1="14" x2="36" y2="50" stroke="#7a7a7a" strokeWidth="0.5" />
        </svg>
      );
  }
}
